import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Check, Bell, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster, toast } from '../components/ui/sonner';
import { requestNotificationPermission, isNotificationEnabled } from '../services/onesignal';
import { trackSignUp } from '../services/analytics';
import WelcomeModal from '../components/common/WelcomeModal';
import MintedLogo from '../components/common/MintedLogo';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useApp();
  
  // Check for referral code from invite link
  const storedReferralCode = localStorage.getItem('minted_referral_code');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    location: 'Australia',
    referralCode: storedReferralCode || ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const passwordStrength = () => {
    const { password } = formData;
    if (password.length === 0) return null;
    if (password.length < 6) return { text: 'Weak', color: 'bg-red-500', width: '33%' };
    if (password.length < 10) return { text: 'Medium', color: 'bg-yellow-500', width: '66%' };
    return { text: 'Strong', color: 'bg-minted-500', width: '100%' };
  };

  const strength = passwordStrength();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service');
      return;
    }

    setLoading(true);
    
    try {
      const result = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        location: formData.location,
        referralCode: formData.referralCode || undefined
      });
      
      if (result.success) {
        // Clear stored referral code after successful registration
        localStorage.removeItem('minted_referral_code');
        toast.success('Account created successfully!');
        
        // Track sign up for analytics
        trackSignUp('email');
        
        // Show welcome modal first
        setShowWelcomeModal(true);
      } else {
        toast.error(result.error || 'Registration failed');
      }
    } catch (error) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableNotifications = async () => {
    setNotificationLoading(true);
    try {
      const granted = await requestNotificationPermission();
      if (granted) {
        toast.success('Notifications enabled! You\'ll be notified about messages, offers and updates.');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setNotificationLoading(false);
      navigate('/');
    }
  };

  const handleSkipNotifications = () => {
    // Mark as dismissed so the general prompt doesn't show again immediately
    localStorage.setItem('pushPromptDismissed', 'true');
    navigate('/');
  };

  const handleWelcomeClose = () => {
    setShowWelcomeModal(false);
    
    // Check if notifications are supported and not already enabled
    const notificationsSupported = typeof window !== 'undefined' && 'Notification' in window;
    const alreadyEnabled = isNotificationEnabled();
    
    if (notificationsSupported && !alreadyEnabled) {
      setShowNotificationPrompt(true);
    } else {
      navigate('/');
    }
  };

  // Show welcome modal after registration
  if (showWelcomeModal) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <WelcomeModal
          isOpen={showWelcomeModal}
          onClose={handleWelcomeClose}
          userName={formData.name}
        />
      </>
    );
  }

  // Show notification prompt modal after welcome
  if (showNotificationPrompt) {
    return (
      <div className="min-h-screen bg-minted-900 flex flex-col items-center justify-center px-6">
        <Toaster position="top-center" richColors />
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="w-16 h-16 bg-minted-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Bell className="w-8 h-8 text-minted-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Stay in the Loop!</h2>
          <p className="text-gray-600 mb-6">
            Get instant alerts when someone messages you, makes an offer, or when your item sells. Never miss a deal!
          </p>
          <div className="space-y-3">
            <Button
              onClick={handleEnableNotifications}
              className="w-full bg-minted-900 hover:bg-minted-700 h-12 text-sm tracking-wider uppercase rounded-sm"
              disabled={notificationLoading}
            >
              {notificationLoading ? 'Enabling...' : 'Enable Notifications'}
            </Button>
            <Button
              onClick={handleSkipNotifications}
              variant="ghost"
              className="w-full text-gray-500 hover:text-gray-700"
              disabled={notificationLoading}
            >
              Maybe Later
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            You can change this anytime in your profile settings
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-minted-900 flex flex-col">
      <Toaster position="top-center" richColors />
      {/* Header */}
      <div className="flex flex-col items-center justify-center px-6 py-8">
        <MintedLogo size="lg" showText={true} glowEffect={true} />
        <p className="text-minted-300 text-xs tracking-widest uppercase mt-3">Create Your Account</p>
      </div>

      {/* Register Form */}
      <div className="flex-1 bg-stone-50 rounded-t-2xl px-6 py-8 shadow-xl overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Your full name"
              value={formData.name}
              onChange={handleChange}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={handleChange}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              name="location"
              type="text"
              placeholder="e.g., Australia"
              value={formData.location}
              onChange={handleChange}
              required
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {strength && (
              <div className="mt-2">
                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${strength.color} transition-all duration-300`}
                    style={{ width: strength.width }}
                  />
                </div>
                <p className={`text-xs mt-1 ${strength.color.replace('bg-', 'text-')}`}>
                  Password strength: {strength.text}
                </p>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="mt-1"
            />
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
            {formData.confirmPassword && formData.password === formData.confirmPassword && (
              <p className="text-xs text-minted-500 mt-1 flex items-center gap-1">
                <Check className="w-3 h-3" /> Passwords match
              </p>
            )}
          </div>

          {/* Referral Code Input - always show input, with validation feedback */}
          <div>
            <Label htmlFor="referralCode">Referral Code (optional)</Label>
            <Input
              id="referralCode"
              name="referralCode"
              data-testid="register-referral-code-input"
              type="text"
              placeholder="Enter a referral code (e.g., ABC1234)"
              value={formData.referralCode}
              onChange={handleChange}
              className="mt-1"
              maxLength={10}
            />
            {/* Show "applied" feedback only when code looks complete (6+ chars) */}
            {formData.referralCode && formData.referralCode.length >= 6 && (
              <div className="bg-minted-50 border border-minted-200 rounded-lg p-2 mt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-minted-600 font-medium">Referral Code Applied</p>
                    <p className="text-sm font-bold text-minted-700">{formData.referralCode}</p>
                  </div>
                  <div className="text-minted-600">
                    <Check className="w-5 h-5" />
                  </div>
                </div>
              </div>
            )}
          </div>

          <label className="flex items-start gap-3 py-2">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="rounded border-gray-300 text-minted-600 focus:ring-minted-500 mt-0.5"
            />
            <span className="text-sm text-gray-600">
              I agree to the{' '}
              <Link to="/terms" className="text-minted-600 hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-minted-600 hover:underline">Privacy Policy</Link>
            </span>
          </label>

          <Button
            type="submit"
            className="w-full bg-minted-900 hover:bg-minted-700 h-12 text-sm tracking-wider uppercase rounded-sm"
            disabled={loading || !agreedToTerms}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>

        {/* Login Link */}
        <p className="text-center text-gray-600 text-sm mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-minted-600 font-medium hover:underline">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;